<template>
  <div>
    <CCard class="mb-4 add-option-card">
      <CCardBody>
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h6 class="mb-0 d-flex align-items-center">
            <CIcon name="cil-list" class="mr-2" />
            <strong>{{ $t('product.options.add_option') }}</strong>
          </h6>
          <CBadge color="light" v-if="selected_option_group">
            {{ $t('product.options.option_group') }}: {{ (typeof selected_option_group === 'object') ? selected_option_group.name : selected_option_group }}
          </CBadge>
        </div>

        <div class="form-row">
          <div class="form-group col-12 col-md-6 mb-3">
            <label class="d-block">{{ $t('product.options.option_group') }}</label>
            <v-select
              :options="all_option_groups"
              v-model="selected_option_group"
              label="name"
              @input="selectingOptionGroup"
              :placeholder="this.$t('global.please_select')"
              :searchable="true"
              :clearable="true"
            >
              <template v-slot:option="option">
                <span>{{ option.name }}</span>
              </template>
              <span slot="no-options">{{ this.$t('global.no_items_found') }}</span>
            </v-select>
            <small class="text-muted d-block mt-1">Choose a group to filter options.</small>
          </div>

          <div class="form-group col-12 col-md-6 mb-3">
            <label class="d-block">{{ $t('product.options.options') }}</label>
            <div class="d-flex">
              <div class="flex-grow-1">
                <v-select
                  :options="filteredAllOptions"
                  v-model="selected_option"
                  label="name"
                  :placeholder="this.$t('global.please_select')"
                  :searchable="true"
                  :clearable="true"
                  :selectable="(option) => !productOptions.some((o) => o.name == option.name &&
                    o.option_group.name == (typeof selected_option_group == 'object'
                      ? selected_option_group.name
                      : selected_option_group)
                  )"
                  :disabled="selected_option_group === null"
                >
                  <template v-slot:option="option">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        {{ option.name }}
                        <CBadge v-if="option.orderable === false" color="warning" class="ml-1" size="sm">
                          {{ $t('product.not_orderable') }}
                        </CBadge>
                      </div>
                      <div v-if="option.product_id"><cite><strong>{{ $t('product.product') }}: {{ option.product.name }}</strong></cite></div>
                    </div>
                  </template>
                  <span slot="no-options">{{ this.$t('global.no_items_found') }}</span>
                </v-select>
              </div>
              <CButton
                color="primary"
                class="ml-2"
                :disabled="!selected_option"
                @click="selectingOption"
                title="Add"
              >
                <CIcon name="cil-plus" class="mr-1" /> Add
              </CButton>
            </div>
            <small class="text-muted d-block mt-1">Search and add an option.</small>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- No items message -->
    <div v-if="productOptions.length === 0" class="text-center my-5">
      <h2> {{ $t('global.no_items_found') }}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" role="img" class="text-danger mb-2 c-icon c-icon-custom-size" width="30"><path fill="var(--ci-primary-color, currentColor)" d="M425.706,86.294A240,240,0,0,0,86.294,425.705,240,240,0,0,0,425.706,86.294ZM256,48A207.1,207.1,0,0,1,391.528,98.345L98.345,391.528A207.1,207.1,0,0,1,48,256C48,141.309,141.309,48,256,48Zm0,416a207.084,207.084,0,0,1-134.986-49.887l293.1-293.1A207.084,207.084,0,0,1,464,256C464,370.691,370.691,464,256,464Z" class="ci-primary"></path></svg>
      </h2>
    </div>

    <!-- Accordion for each option group -->
    <div v-if="productOptions.length > 0" id="option-groups-container">
      <CCard
        v-for="(group, groupIndex) in groupedOptions"
        :key="`group-${group.id}`"
        :data-group-id="group.id"
        class="mb-2 option-group-card"
      >
        <CCardHeader class="accordion-header px-0">
          <CButton
            block
            color="link"
            class="text-left"
            @click="toggleAccordion(groupIndex)"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <CIcon name="cil-move" :data-group-id="group.id" class="group-move-icon mr-2" />
                <h6 class="mb-0">
                  <strong>{{ group.name }}</strong>
                  <CBadge color="info" class="ml-2">{{ group.options.length }} {{ $t('product.options.options') }}</CBadge>
                </h6>
              </div>
              <CIcon
                :name="activeAccordions.includes(groupIndex) ? 'cil-chevron-top' : 'cil-chevron-bottom'"
              />
            </div>
          </CButton>
        </CCardHeader>

        <CCollapse :show="activeAccordions.includes(groupIndex)" :id="`collapse-${group.id}`">
          <CCardBody class="p-0">
            <div class="table-options table-responsive" :id="`option-group-${group.id}`">
              <table class="table table-sm mb-0">
                <colgroup>
                  <col>
                  <col>
                  <col>
                  <col>
                  <col>
                  <col span="2" style="background-color: #F8F0DF;">
                  <col>
                  <col span="2" style="background-color: #C4EBD1aa;">
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col" style="min-width: 60px;width: 60px;text-align: center"></th>
                    <th scope="col" style="min-width: 36px;width: 36px;text-align: center"></th>
                    <th scope="col" style="min-width: 100px;width: 100px;text-align: center">{{ $t('global.active') }}</th>
                    <th scope="col" style="min-width: 180px;">{{ $t('product.options.option') }}</th>
                    <th scope="col" style="min-width: 140px;width: 140px">{{ $t('product.options.impact_on_stock') }}</th>
                    <th scope="col" style="min-width: 160px;width: 160px;">{{ $t('product.options.price_impact') }}</th>
                    <th scope="col" style="min-width: 140px;width: 140px">{{ $t('product.options.amount') }}</th>
                    <th scope="col" style="min-width: 140px;width: 140px">{{ $t('product.options.price_multiplier_for_extras') }}</th>
                    <th scope="col" style="min-width: 180px;width: 180px">{{ $t('product.product') }}</th>
                    <th scope="col" style="min-width: 260px;width: 260px;">{{ $t('product.net_content') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in group.options" :key="`option-${group.id}-${item.id}`" :class="{ 'option-row--product-deleted': item.product?.deleted_at }">
                    <!-- Position / Move -->
                    <td class="move-item text-center">
                      <span class="mr-2">{{ item.pivot.position }}</span>
                      <CIcon name="cil-move" :data-id="item.id" class="move-icon align-text-bottom" />
                    </td>

                    <!-- Actions -->
                    <td class="py-1 px-0 text-center">
                      <CButton
                        variant="ghost"
                        size="sm"
                        color="danger"
                        @click="deleteItem(item, item.originalIndex)"
                      >
                        <CIcon name="cil-trash"/>
                      </CButton>
                    </td>

                    <!-- Active -->
                    <td class="text-center">
                      <CSwitch
                        :checked.sync="item.pivot.active"
                        :labelOn="$t('global.switch_yes')"
                        :labelOff="$t('global.switch_no')"
                        color="success"
                        shape="pill"
                        :lazy="false"
                        :disabled="item.orderable === false"
                      />
                    </td>

                    <!-- Option Name -->
                    <td>
                      {{ item.name }}
                      <CBadge v-if="item.orderable === false" color="warning" class="ml-1" size="sm">
                        {{ $t('product.not_orderable') }}
                      </CBadge>
                    </td>

                    <!-- Impact On Stock -->
                    <td v-if="item.product" v-html="nullChar"></td>
                    <td v-else>
                      <CInput
                        class="mb-0 w-120px"
                        :lazy="false"
                        :value.sync="item.pivot.impact_on_stock"
                        placeholder="0.00"
                        type="number"
                        step="0.01"
                        pattern="^\d+(?:\.\d{1,2})?$"
                        onblur="(function(el){el.value=parseFloat(el.value).toFixed(2);})(this)"
                      >
                        <template #prepend-content><CIcon name="cil-x" /></template>
                      </CInput>
                    </td>

                    <!-- Amount Type -->
                    <td>
                      <CSelect
                        class="mb-0"
                        :lazy="false"
                        :placeholder="$t('global.please_select')"
                        :value.sync="item.pivot.amount_type"
                        :options="[
                          { value: 'multiplier', label: $t('product.options.multiplier') },
                          { value: 'fixed', label: $t('product.options.fixed') },
                          { value: 'additional', label: $t('product.options.additional') },
                        ]"
                      />
                    </td>

                    <!-- Amount -->
                    <td>
                      <CInput
                        class="mb-0 w-120px"
                        :lazy="false"
                        :value.sync="item.pivot.amount"
                        placeholder="0.00"
                        type="number"
                        step="0.01"
                        pattern="^\d+(?:\.\d{1,2})?$"
                        onblur="(function(el){el.value=parseFloat(el.value).toFixed(2);})(this)"
                      >
                        <template v-if="item.pivot.amount_type == 'multiplier'" #prepend-content><CIcon name="cil-x" /></template>
                        <template v-else #prepend-content><CIcon name="cil-euro" /></template>
                      </CInput>
                    </td>

                    <!-- Price Multiplier for Extras -->
                    <td>
                      <CInput
                        class="mb-0 w-120px"
                        :lazy="false"
                        :value.sync="item.pivot.price_multiplier_for_extras"
                        placeholder="0.00"
                        type="number"
                        step="0.01"
                        pattern="^\d+(?:\.\d{1,2})?$"
                        onblur="(function(el){el.value=parseFloat(el.value).toFixed(2);})(this)"
                      >
                        <template #prepend-content><CIcon name="cil-x" /></template>
                      </CInput>
                    </td>

                    <!-- Product -->
                    <td v-if="item.product">
                      <a
                        href="#"
                        class="text-primary"
                        :class="{ 'option-name--deleted': item.product.deleted_at }"
                        @click.prevent="openProduct(item.product_id)"
                      >
                        {{ item.product.name }}
                      </a>
                      <CBadge v-if="item.product.deleted_at" color="danger" class="ml-1 deleted-product-badge">{{ $t('product.deleted') }}</CBadge>
                    </td>
                    <td v-else v-html="nullChar"></td>

                    <!-- Net Content -->
                    <td v-if="item.product">
                      <div class="d-flex flex-row">
                        <CInput
                          style="width: 100px;"
                          class="mb-0 mr-1"
                          :lazy="false"
                          :value.sync="item.pivot.net_content"
                          placeholder="0.00"
                          type="number"
                          step="0.01"
                          pattern="^\d+(?:\.\d{1,2})?$"
                          onblur="(function(el){el.value=parseFloat(el.value).toFixed(2);})(this)"
                        />

                        <CSelect
                          style="width: 150px;"
                          class="mb-0"
                          :placeholder="$t('global.please_select')"
                          :lazy="false"
                          :value.sync="item.pivot.net_content_unit_code"
                          :options="measure_codes"
                        />
                      </div>
                    </td>
                    <td v-else v-html="nullChar"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CCardBody>
        </CCollapse>
      </CCard>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import Sortable from "sortablejs";

export default {
  name: "product-options",
  components: {
    vSelect,
  },

  props: {
    options: { type: Array, default() { return []; } },
    all_options: { type: Array, default() { return []; } },
    all_option_groups: { type: Array, default() { return []; } },
    measure_codes: { type: Array, default() { return []; } }
  },

  data() {
    return {
      selected_option: null,
      selected_option_group: null,
      activeAccordions: [],
      sortables: [],
      groupSortable: null,
      isInitialized: false,
    };
  },

  computed: {
    nullChar: { get() { return process.env.VUE_APP_NULL_CHAR } },
    productOptions: {
      get() {
        return this.options;
      },
      set(newValue) {
        this.$emit("update:options", newValue);
      },
    },

    allOptions: {
      get() {
        return this.all_options;
      },
      set(newValue) {
        this.$emit("update:all_options", newValue);
      },
    },

    filteredAllOptions() {
      return this.selected_option_group
        ? this.allOptions.filter((option) => {
            return this.selected_option_group.id
              ? option.option_group.id === this.selected_option_group.id
              : option.option_group.name ===
                  (typeof this.selected_option_group == "object"
                    ? this.selected_option_group.name
                    : this.selected_option_group);
          })
        : [];
    },

    groupedOptions() {
      const groups = {};

      this.productOptions.forEach((option, index) => {
        const groupName = option.option_group.name;
        const groupId = option.option_group.id || groupName;

        if (!groups[groupId]) {
          groups[groupId] = {
            id: groupId,
            name: groupName,
            group_position: option.pivot.group_position || 0,
            options: []
          };
        }

        // Store original index for deletion
        groups[groupId].options.push({
          ...option,
          originalIndex: index
        });
      });

      // Sort groups by group_position
      const sortedGroups = Object.values(groups).sort((a, b) => a.group_position - b.group_position);

      // Sort options within each group by position
      sortedGroups.forEach(group => {
        group.options.sort((a, b) => (a.pivot.position || 0) - (b.pivot.position || 0));
      });

      return sortedGroups;
    },
  },

  mounted() {
    this.$root.$on('product-updated', this.handleProductUpdated);

    // Open all accordions by default
    this.activeAccordions = this.groupedOptions.map((_, index) => index);
    this.$nextTick(() => {
      this.initSortables();
    });
  },

  beforeDestroy() {
    this.$root.$off('product-updated', this.handleProductUpdated);

    // Clean up sortables
    this.sortables.forEach(sortable => {
      if (sortable) sortable.destroy();
    });
    if (this.groupSortable) {
      this.groupSortable.destroy();
    }
  },

  watch: {
    productOptions: {
      handler(newVal) {
        // Initialize positions only once when options are first loaded
        if (!this.isInitialized && newVal && newVal.length > 0) {
          const needsInit = newVal.some(opt => !opt.pivot.group_position || opt.pivot.group_position === 0);
          if (needsInit) {
            // Initialize positions immediately (not in nextTick to avoid delays)
            this.initializePositions();
            this.isInitialized = true;
          } else {
            this.isInitialized = true;
          }
        }
      },
      immediate: true
    },

    groupedOptions: {
      handler(newVal) {
        // Ensure all accordions are open when new groups are added
        this.activeAccordions = newVal.map((_, index) => index);
        this.$nextTick(() => {
          this.initSortables();
        });
      },
      deep: true
    }
  },

  methods: {
    initializePositions() {
      if (this.productOptions.length === 0) return;

      // Group options by option_group
      const groups = {};
      this.productOptions.forEach((option) => {
        const groupId = option.option_group.id || option.option_group.name;
        if (!groups[groupId]) {
          groups[groupId] = [];
        }
        groups[groupId].push(option);
      });

      const groupList = Object.values(groups);

      // Check if all group_positions are 0 or uninitialized
      const allGroupPositionsZero = this.productOptions.every(opt => !opt.pivot.group_position || opt.pivot.group_position === 0);

      // Create position map for each group
      const positionMap = {};
      groupList.forEach((groupOptions) => {
        const allPositionsZero = groupOptions.every(opt => !opt.pivot.position || opt.pivot.position === 0);

        if (allPositionsZero) {
          groupOptions.forEach((option, index) => {
            positionMap[option.id] = index + 1;
          });
        }
      });

      // Update all options in one pass
      let needsUpdate = allGroupPositionsZero || Object.keys(positionMap).length > 0;

      if (needsUpdate) {
        const updatedOptions = this.productOptions.map(option => {
          const groupId = option.option_group.id || option.option_group.name;
          const groupIndex = groupList.findIndex(g => {
            const firstOption = g[0];
            const firstGroupId = firstOption.option_group.id || firstOption.option_group.name;
            return firstGroupId === groupId;
          });

          const newGroupPosition = allGroupPositionsZero ? groupIndex + 1 : option.pivot.group_position;
          const newPosition = positionMap[option.id] || option.pivot.position;

          return {
            ...option,
            pivot: {
              ...option.pivot,
              group_position: newGroupPosition,
              position: newPosition || 0
            }
          };
        });

        this.productOptions = updatedOptions;
      }
    },

    initSortables() {
      // Destroy existing sortables
      this.sortables.forEach(sortable => {
        if (sortable) sortable.destroy();
      });
      this.sortables = [];

      if (this.groupSortable) {
        this.groupSortable.destroy();
        this.groupSortable = null;
      }

      // Create sortable for option groups
      this.initGroupSortable();

      // Create sortable for options within each group
      this.groupedOptions.forEach((group) => {
        try {
          const tbody = this.$el.querySelector(`#option-group-${group.id} tbody`);
          if (!tbody) return;

          let draggedItemId = null;
          let newIndex = null;

          const sortable = Sortable.create(tbody, {
            handle: "svg.move-icon",
            chosenClass: "table-selected",
            animation: 150,
            easing: "cubic-bezier(1, 0, 0, 1)",
            onSort: (evt) => {
              const itemEl = evt.item;
              newIndex = evt.newIndex;
              const handleEl = itemEl.querySelector('svg.move-icon');
              draggedItemId = handleEl ? handleEl.getAttribute('data-id') : null;
            },
            store: {
              set: () => {
                if (draggedItemId === null || newIndex === null) return;
                this.updateOptionPosition(parseInt(draggedItemId), newIndex, group.id);
              }
            }
          });

          this.sortables.push(sortable);
        } catch (e) {
          // fail silently
        }
      });
    },

    initGroupSortable() {
      try {
        const container = this.$el.querySelector('#option-groups-container');
        if (!container) return;

        let draggedGroupId = null;
        let newIndex = null;

        this.groupSortable = Sortable.create(container, {
          handle: "svg.group-move-icon",
          chosenClass: "group-selected",
          animation: 150,
          easing: "cubic-bezier(1, 0, 0, 1)",
          onSort: (evt) => {
            const itemEl = evt.item;
            newIndex = evt.newIndex;
            const handleEl = itemEl.querySelector('svg.group-move-icon');
            draggedGroupId = handleEl ? handleEl.getAttribute('data-group-id') : null;
          },
          store: {
            set: () => {
              if (draggedGroupId === null || newIndex === null) return;
              this.updateGroupPosition(draggedGroupId, newIndex);
            }
          }
        });
      } catch (e) {
        // fail silently
      }
    },

     updateGroupPosition(groupId, newIndex) {
      // Dizi index'i 0'dan başlıyor, ancak bizim 0. elemanın position'u 1'den başlıyor.
      newIndex++;

      // Find all options in this group
      const groupOptions = this.productOptions.filter(o => {
        const oGroupId = o.option_group.id || o.option_group.name;
        return oGroupId == groupId; // Use == for type coercion
      });

      if (groupOptions.length === 0) {
        return;
      }

      const oldIndex = groupOptions[0].pivot.group_position || 0;

      if (oldIndex === newIndex) {
        return;
      }

      // Create a new array with updated values for better reactivity
      const updatedOptions = this.productOptions.map(option => {
        const optionGroupId = option.option_group.id || option.option_group.name;
        const currentGroupPosition = option.pivot.group_position || 0;

        // Clone the option and its pivot
        const updatedOption = {
          ...option,
          pivot: { ...option.pivot }
        };

        // If this option is in the moved group, set to new position
        if (optionGroupId == groupId) { // Use == for type coercion
          updatedOption.pivot.group_position = newIndex;
        }
        // If moving down (old=1, new=3): decrement positions 2,3 -> 1,2
        else if (newIndex > oldIndex && currentGroupPosition > oldIndex && currentGroupPosition <= newIndex) {
          updatedOption.pivot.group_position = currentGroupPosition - 1;
        }
        // If moving up (old=3, new=1): increment positions 1,2 -> 2,3
        else if (newIndex < oldIndex && currentGroupPosition < oldIndex && currentGroupPosition >= newIndex) {
          updatedOption.pivot.group_position = currentGroupPosition + 1;
        }

        return updatedOption;
      });

      // Update the array to trigger reactivity
      this.productOptions = updatedOptions;
    },

    updateOptionPosition(optionId, newIndex, groupId) {
      if (isNaN(optionId) || isNaN(newIndex)) return;

      // Dizi index'i 0'dan başlıyor, ancak bizim 0. elemanın position'u 1'den başlıyor.
      newIndex++;

      const movedOption = this.productOptions.find(o => o.id == optionId);
      if (!movedOption) return;

      const oldIndex = movedOption.pivot.position || 0;

      if (oldIndex === newIndex) return; // No change needed

      // Create a new array with updated values for better reactivity
      const updatedOptions = this.productOptions.map(option => {
        const optionGroupId = option.option_group.id || option.option_group.name;
        const inSameGroup = optionGroupId === groupId;
        const currentPosition = option.pivot.position || 0;

        // Clone the option and its pivot
        const updatedOption = {
          ...option,
          pivot: { ...option.pivot }
        };

        // If this is the moved option, set new position
        if (option.id === optionId) {
          updatedOption.pivot.position = newIndex;
        }
        // If in same group and affected by the move
        else if (inSameGroup) {
          if (newIndex > oldIndex && currentPosition > oldIndex && currentPosition <= newIndex) {
            updatedOption.pivot.position = currentPosition - 1;
          } else if (newIndex < oldIndex && currentPosition < oldIndex && currentPosition >= newIndex) {
            updatedOption.pivot.position = currentPosition + 1;
          }
        }

        return updatedOption;
      });

      // Update the array to trigger reactivity
      this.productOptions = updatedOptions;
    },

    toggleAccordion(index) {
      const accordionIndex = this.activeAccordions.indexOf(index);
      if (accordionIndex > -1) {
        this.activeAccordions.splice(accordionIndex, 1);
      } else {
        this.activeAccordions.push(index);
      }
    },

    selectingOptionGroup() {
      if (this.selected_option_group) {
        this.selected_option = null
      }
    },

    selectingOption() {
      if (this.selected_option) {
        this.addProductOptions(
          typeof this.selected_option == "object"
            ? this.selected_option
            : { name: this.selected_option }
        );
      }
    },

    openProduct(productId) {
      if (this.$listeners["open-product"]) {
        this.$emit("open-product", productId);
        return;
      }

      this.$store.commit('set', ['productEditModalId', productId]);
    },

    handleProductUpdated(product) {
      if (!product?.id) return;

      const updatedOptions = this.productOptions.map((option) => {
        const optionProductId = option.product_id || option.product?.id;
        if (Number(optionProductId) !== Number(product.id)) {
          return option;
        }

        const incomingPivot = Object.prototype.hasOwnProperty.call(product, 'pivot') ? product.pivot : undefined;

        return {
          ...option,
          product: {
            ...(option.product || {}),
            ...product,
          },
          pivot: option.pivot
            ? { ...option.pivot, ...(incomingPivot || {}) }
            : (incomingPivot || null),
        };
      });

      this.productOptions = updatedOptions;
    },

    addProductOptions(option) {
      const option_group = option.option_group
        ? option.option_group
        : typeof this.selected_option_group == "object"
        ? this.selected_option_group
        : { name: this.selected_option_group };

      // Bu option yeni eklendiyse allOptions'a da ekle.
      if (!option.id) {
        this.allOptions = this.allOptions
          .filter((o) => o.name !== option.name)
          .concat([{ name: option.name, option_group: option_group }]);
      }

      // Aynı option ikinci defa productOptions'a eklenemez. (Enter'a basarak eklenebildiği için bu kontrol eklendi.)
      if (this.productOptions.findIndex((o) => o.name == option.name && o.option_group.name == option_group.name) !== -1) {
        return;
      }

      // Calculate position and group_position
      const groupId = option_group.id || option_group.name;
      const existingGroupOptions = this.productOptions.filter(o => {
        const oGroupId = o.option_group.id || o.option_group.name;
        return oGroupId === groupId;
      });

      let position = 1;
      let group_position = 1;

      if (existingGroupOptions.length > 0) {
        // Group exists, get max position in this group
        const maxPosition = Math.max(...existingGroupOptions.map(o => o.pivot.position || 0));
        position = maxPosition + 1;
        group_position = existingGroupOptions[0].pivot.group_position;
      } else {
        // New group, get max group_position from all groups
        if (this.productOptions.length > 0) {
          const maxGroupPosition = Math.max(...this.productOptions.map(o => o.pivot.group_position || 0));
          group_position = maxGroupPosition + 1;
        }
      }

      this.productOptions.push({
        id: option.id,
        name: option.name,
        option_group: option_group,
        product_id: option.product_id,
        product: option.product,
        pivot: {
          amount: 0,
          amount_type: "fixed",
          impact_on_stock: 1.0,
          price_multiplier_for_extras: 1.0,
          net_content: option.product ? option.product.net_content : null,
          net_content_unit_code: option.product ? option.product.net_content_unit_code : null,
          active: true,
          position: position,
          group_position: group_position,
        },
      });
    },

    deleteItem(item, index) {
      this.productOptions.splice(index, 1);
      this.selected_option = null;
    },
  },
};
</script>

<style>
.table-options th {
  border-top: none;
}
.table-options th, .table-options td {
  padding: 0 10px;
  vertical-align: middle !important;
}
.table-options thead tr {
  height: 32px;
}
.w-120px {
  width: 120px;
}
.accordion-header .btn-link {
  text-decoration: none !important;
}
.accordion-header .btn-link:hover {
  text-decoration: none !important;
}

/* Sortable Styles */
.move-item {
  cursor: move;
}
.move-icon {
  cursor: move;
  color: #768192;
}
.move-icon:hover {
  color: #321fdb;
}
.table-selected {
  background-color: #e3f2fd !important;
}

/* Group Sortable Styles */
.group-move-icon {
  cursor: move;
  color: #768192;
  font-size: 1.2rem;
}
.group-move-icon:hover {
  color: #321fdb;
}
.group-selected {
  opacity: 0.5;
}
.option-group-card {
  transition: all 0.3s ease;
}
/* Add Option Card styles */
.add-option-card {
  border: 1px solid #e7e9f0;
  box-shadow: 0 2px 6px rgba(50, 31, 219, 0.06);
  border-radius: .5rem;
  background: linear-gradient(180deg, #f8faff 0%, #ffffff 100%);
}
.add-option-card .vs__dropdown-toggle {
  border-color: #cfd6e4;
}
.add-option-card .vs__search::placeholder {
  color: #9aa2b1;
}

/* Deleted product option row */
.option-row--product-deleted {
  background: #fff5f5 !important;
  opacity: 0.75;
}
.option-row--product-deleted td {
  color: #999;
}
.deleted-product-badge {
  font-size: 0.62rem;
  padding: 0.1em 0.38em;
  vertical-align: middle;
}
.option-name--deleted {
  text-decoration: line-through;
  color: #999;
}
</style>
